import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Loader2, Search, Users, UserCog, Mail, Hash, MapPin, ChevronDown, ChevronRight, RefreshCw, Edit, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminUserManagement({ userInfo }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [npnFilter, setNpnFilter] = useState('all'); // all, with-npn, without-npn
  const [locationFilter, setLocationFilter] = useState('all'); // all, with-location, without-location
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Edit Modal State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    agent_npn: '',
    ghlLocation_id: ''
  });
  const [locations, setLocations] = useState([]);
  const [locationSearch, setLocationSearch] = useState('');
  const [npnVerification, setNpnVerification] = useState({ status: '', data: null });
  const [verifyingNpn, setVerifyingNpn] = useState(false);
  
  // Confirmation Modal State
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/users', {
        params: { 
          user_id: userInfo?.user_id,
          location_id: userInfo?.location_id
        }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [userInfo]);

  // Fetch locations for dropdown
  const fetchLocations = async () => {
    try {
      const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/locations', {
        params: { 
          user_id: userInfo?.user_id,
          location_id: userInfo?.location_id
        }
      });
      setLocations(response.data);
    } catch (err) {
      console.error('Error fetching locations:', err);
      toast.error('Failed to load locations');
    }
  };

  // Verify NPN
  const verifyNPN = async (npn) => {
    // Convert to string and trim
    const npnStr = npn ? String(npn).trim() : '';
    
    if (!npnStr || npnStr === '') {
      setNpnVerification({ status: '', data: null });
      return;
    }

    try {
      setVerifyingNpn(true);
      setNpnVerification({ status: 'verifying', data: null });
      
      const response = await axios.get('https://api1.simplyworkcrm.com/api:JBYRWqH3/auth/verifyNPN', {
        params: { npn: npnStr }
      });

      if (response.data.status === 'active') {
        setNpnVerification({ 
          status: 'success', 
          data: response.data 
        });
        toast.success(`NPN verified: ${response.data.first_name} ${response.data.last_name}`);
      } else {
        setNpnVerification({ status: 'inactive', data: response.data });
        toast.error('NPN status is not active');
      }
    } catch (err) {
      console.error('Error verifying NPN:', err);
      setNpnVerification({ status: 'error', data: null });
      toast.error('Failed to verify NPN. Please check the number and try again.');
    } finally {
      setVerifyingNpn(false);
    }
  };

  // Handle edit user
  const handleEditUser = async (user) => {
    setEditingUser(user);
    setFormData({
      agent_npn: user.agent_npn ? String(user.agent_npn) : '',
      ghlLocation_id: user.ghlLocation_name || ''
    });
    setNpnVerification({ status: '', data: null });
    setLocationSearch('');
    
    // Fetch locations
    await fetchLocations();
    
    // If user has NPN, verify it
    if (user.agent_npn) {
      verifyNPN(user.agent_npn);
    }
    
    setShowEditModal(true);
  };

  // Handle submit
  const handleSubmit = async () => {
    // Validate NPN if provided
    if (formData.agent_npn && npnVerification.status !== 'success') {
      toast.error('Please verify the Agent NPN before submitting');
      return;
    }

    setShowConfirmModal(true);
  };

  // Confirm and save changes
  const confirmSave = async () => {
    try {
      setSubmitting(true);
      
      const updateData = {
        system_id: editingUser.id,
        agent_npn: formData.agent_npn || null,
        ghlLocation_id: (formData.ghlLocation_id && formData.ghlLocation_id !== 'none') ? formData.ghlLocation_id : null
      };

      await axios.post(
        'https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/users/update',
        updateData,
        {
          params: {
            user_id: userInfo?.user_id,
            location_id: userInfo?.location_id
          }
        }
      );

      toast.success('User updated successfully');
      setShowConfirmModal(false);
      setShowEditModal(false);
      fetchUsers(); // Refresh the list
    } catch (err) {
      console.error('Error updating user:', err);
      toast.error('Failed to update user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter users
  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    // Search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(user =>
        user.first_name?.toLowerCase().includes(searchLower) ||
        user.last_name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.agent_npn?.toString().includes(searchLower) ||
        user.ghlLocation_name?.toLowerCase().includes(searchLower)
      );
    }

    // NPN filter
    if (npnFilter === 'with-npn') {
      filtered = filtered.filter(user => user.agent_npn);
    } else if (npnFilter === 'without-npn') {
      filtered = filtered.filter(user => !user.agent_npn);
    }

    // Location filter
    if (locationFilter === 'with-location') {
      filtered = filtered.filter(user => user.ghlLocation_name);
    } else if (locationFilter === 'without-location') {
      filtered = filtered.filter(user => !user.ghlLocation_name);
    }

    return filtered;
  }, [users, searchQuery, npnFilter, locationFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  const toggleRow = (userId) => {
    setExpandedRows(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <UserCog className="w-16 h-16 text-neutral-300" />
        <p className="text-neutral-500 text-lg">{error}</p>
        <Button onClick={fetchUsers} className="bg-[#d4af37] hover:bg-[#b8941e]">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-neutral-800">User Management</h2>
        <p className="text-neutral-600 mt-1">Manage internal staff and user accounts</p>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-3 gap-6">
        {/* Total Users */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#d4af37] via-[#f4e5b8] to-[#fffef0] p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-[#d4af37]/20">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-neutral-700 uppercase tracking-wider">Total Users</p>
              <Users className="w-5 h-5 text-[#b8941e]" />
            </div>
            <p className="text-4xl font-black text-[#b8941e] mb-1">{filteredUsers.length}</p>
            <p className="text-xs text-neutral-600 font-medium">Active Accounts</p>
          </div>
          <div className="absolute -right-4 -bottom-4 opacity-10">
            <div className="w-24 h-24 bg-[#d4af37] rounded-full"></div>
          </div>
        </div>

        {/* With Agent NPN */}
        <div className="relative overflow-hidden bg-gradient-to-br from-emerald-400 via-green-300 to-green-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-green-900 uppercase tracking-wider">With Agent NPN</p>
              <Hash className="w-5 h-5 text-green-700" />
            </div>
            <p className="text-4xl font-black text-green-900 mb-1">
              {users.filter(u => u.agent_npn).length}
            </p>
            <p className="text-xs text-green-700 font-medium">Licensed Agents</p>
          </div>
        </div>

        {/* With Location */}
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-400 via-blue-300 to-blue-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-blue-900 uppercase tracking-wider">With Location</p>
              <MapPin className="w-5 h-5 text-blue-700" />
            </div>
            <p className="text-4xl font-black text-blue-900 mb-1">
              {users.filter(u => u.ghlLocation_name).length}
            </p>
            <p className="text-xs text-blue-700 font-medium">Assigned Locations</p>
          </div>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-xl">
        <CardHeader>
          {/* Filters */}
          <div className="flex gap-3 items-end">
            <div style={{ width: '40vw' }}>
              <label className="text-xs text-neutral-500 font-medium mb-1 block">Quick Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search by name, email, NPN, or location..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-9 rounded-xl"
                />
              </div>
            </div>

            {/* Filter by NPN */}
            <div style={{ width: '15vw' }}>
              <label className="text-xs text-neutral-500 font-medium mb-1 block">Filter by NPN</label>
              <Select
                value={npnFilter}
                onValueChange={(value) => {
                  setNpnFilter(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="with-npn">With NPN</SelectItem>
                  <SelectItem value="without-npn">Without NPN</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filter by Location */}
            <div style={{ width: '15vw' }}>
              <label className="text-xs text-neutral-500 font-medium mb-1 block">Filter by Location</label>
              <Select
                value={locationFilter}
                onValueChange={(value) => {
                  setLocationFilter(value);
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="with-location">With Location</SelectItem>
                  <SelectItem value="without-location">Without Location</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button
              onClick={fetchUsers}
              variant="outline"
              className="rounded-xl"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {(searchQuery || npnFilter !== 'all' || locationFilter !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setNpnFilter('all');
                setLocationFilter('all');
                setCurrentPage(1);
              }}
              className="mt-2 rounded-xl"
            >
              Clear All Filters
            </Button>
          )}
        </CardHeader>

        <CardContent>
          {/* Showing Info & Page Size Selector */}
          <div className="flex items-center justify-between text-sm text-neutral-600 mb-4">
            <div className="flex items-center gap-3">
              <span>
                Showing {filteredUsers.length === 0 ? 0 : startIndex + 1} - {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-neutral-500">Rows per page:</span>
                <Select 
                  value={itemsPerPage.toString()} 
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-20 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {totalPages > 1 && <span>Page {currentPage} of {totalPages}</span>}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <div className="max-h-[600px] overflow-y-auto rounded-xl border border-neutral-100">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto mb-3 text-neutral-300" />
                  <p className="text-neutral-600">No users found</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-neutral-50 to-neutral-100 sticky top-0 z-10">
                    <tr>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5 w-8 first:rounded-tl-xl"></th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Name</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Email</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Agent NPN</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5">Location</th>
                      <th className="text-left text-xs font-semibold text-neutral-700 px-4 py-3.5 last:rounded-tr-xl">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100 bg-white">
                    {currentUsers.map((user) => (
                      <React.Fragment key={user.id}>
                        <tr className="hover:bg-neutral-50 transition-all duration-200">
                          <td className="px-4 py-3">
                            <button
                              onClick={() => toggleRow(user.id)}
                              className="text-neutral-400 hover:text-[#d4af37]"
                            >
                              {expandedRows[user.id] ? (
                                <ChevronDown className="w-5 h-5" />
                              ) : (
                                <ChevronRight className="w-5 h-5" />
                              )}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-neutral-800">
                              {user.first_name} {user.last_name}
                            </p>
                            <p className="text-xs text-neutral-500">ID: {user.ghl_userid}</p>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-neutral-400" />
                              <span className="text-sm text-neutral-700">{user.email}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {user.agent_npn ? (
                              <Badge className="bg-green-100 text-green-800 hover:bg-green-100 text-xs">
                                {user.agent_npn}
                              </Badge>
                            ) : (
                              <span className="text-xs text-neutral-400">N/A</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {user.ghlLocation_name ? (
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-blue-600" />
                                <span className="text-sm text-neutral-700">
                                  {user.ghlLocation_name.replace(/"/g, '')}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-neutral-400">Not assigned</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              onClick={() => handleEditUser(user)}
                              size="sm"
                              className="bg-[#d4af37] hover:bg-[#b8941e] text-white rounded-lg h-8 w-8 p-0"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>

                        {/* Expanded Row */}
                        {expandedRows[user.id] && (
                          <tr>
                            <td colSpan="6" className="bg-neutral-50 px-4 py-6">
                              <h4 className="text-sm font-semibold text-neutral-700 mb-3">User Details</h4>
                              <div className="grid grid-cols-3 gap-6">
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Full Name</p>
                                  <p className="text-sm text-neutral-800 font-medium">
                                    {user.first_name} {user.last_name}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Email Address</p>
                                  <p className="text-sm text-neutral-800">{user.email}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">GHL User ID</p>
                                  <p className="text-sm text-neutral-800 font-mono">{user.ghl_userid}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">Agent NPN</p>
                                  <p className="text-sm text-neutral-800">
                                    {user.agent_npn || 'Not assigned'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">GHL Location</p>
                                  <p className="text-sm text-neutral-800">
                                    {user.ghlLocation_name ? user.ghlLocation_name.replace(/"/g, '') : 'Not assigned'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-neutral-500 mb-1">System ID</p>
                                  <p className="text-xs text-neutral-600 font-mono break-all">{user.id}</p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-200">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="rounded-lg"
              >
                Previous
              </Button>
              <span className="text-sm text-neutral-600">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="rounded-lg"
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Edit User</DialogTitle>
            {editingUser && (
              <DialogDescription className="space-y-1 pt-2">
                <p className="text-sm"><span className="font-medium">Name:</span> {editingUser.first_name} {editingUser.last_name}</p>
                <p className="text-sm"><span className="font-medium">GHL User ID:</span> {editingUser.ghl_userid}</p>
                <p className="text-sm"><span className="font-medium">Email:</span> {editingUser.email}</p>
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Agent NPN Field */}
            <div className="space-y-2">
              <Label htmlFor="agent_npn">Agent NPN</Label>
              <div className="flex gap-2">
                <Input
                  id="agent_npn"
                  type="text"
                  placeholder="Enter Agent NPN"
                  value={formData.agent_npn}
                  onChange={(e) => {
                    setFormData({ ...formData, agent_npn: e.target.value });
                    setNpnVerification({ status: '', data: null });
                  }}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={() => verifyNPN(formData.agent_npn)}
                  disabled={!formData.agent_npn || verifyingNpn}
                  variant="outline"
                  className="min-w-[100px]"
                >
                  {verifyingNpn ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify NPN'
                  )}
                </Button>
              </div>
              
              {/* NPN Verification Status */}
              {npnVerification.status === 'success' && (
                <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 p-2 rounded">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Verified: {npnVerification.data.first_name} {npnVerification.data.last_name}</span>
                </div>
              )}
              {npnVerification.status === 'inactive' && (
                <div className="flex items-center gap-2 text-orange-600 text-sm bg-orange-50 p-2 rounded">
                  <AlertCircle className="w-4 h-4" />
                  <span>NPN status is not active</span>
                </div>
              )}
              {npnVerification.status === 'error' && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-2 rounded">
                  <XCircle className="w-4 h-4" />
                  <span>Failed to verify NPN. Please check the number.</span>
                </div>
              )}
            </div>

            {/* GHL Location Field */}
            <div className="space-y-2">
              <Label htmlFor="ghlLocation">GHL Location</Label>
              <Select
                value={formData.ghlLocation_id}
                onValueChange={(value) => setFormData({ ...formData, ghlLocation_id: value })}
              >
                <SelectTrigger id="ghlLocation">
                  <SelectValue placeholder="Select a location" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <div className="sticky top-0 z-10 bg-white p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                      <Input
                        type="text"
                        placeholder="Search locations..."
                        value={locationSearch}
                        onChange={(e) => setLocationSearch(e.target.value)}
                        className="pl-8 h-8"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <SelectItem value="none">Not assigned</SelectItem>
                  {locations
                    .filter(loc => 
                      !locationSearch || 
                      loc.name.toLowerCase().includes(locationSearch.toLowerCase())
                    )
                    .map(location => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name.replace(/"/g, '')}
                      </SelectItem>
                    ))
                  }
                  {locations.filter(loc => 
                    !locationSearch || 
                    loc.name.toLowerCase().includes(locationSearch.toLowerCase())
                  ).length === 0 && locationSearch && (
                    <div className="py-6 text-center text-sm text-neutral-500">
                      No locations found
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              className="bg-[#d4af37] hover:bg-[#b8941e]"
              disabled={formData.agent_npn && npnVerification.status !== 'success'}
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Confirm Changes</DialogTitle>
            <DialogDescription>
              Are you sure you want to update this user's information?
            </DialogDescription>
          </DialogHeader>

          {editingUser && (
            <div className="space-y-3 py-4">
              <div>
                <p className="text-sm font-medium text-neutral-700">User:</p>
                <p className="text-sm text-neutral-600">{editingUser.first_name} {editingUser.last_name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-700">Agent NPN:</p>
                <p className="text-sm text-neutral-600">
                  {formData.agent_npn || 'Not assigned'}
                  {npnVerification.data && (
                    <span className="text-green-600 ml-2">
                      ✓ ({npnVerification.data.first_name} {npnVerification.data.last_name})
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-700">GHL Location:</p>
                <p className="text-sm text-neutral-600">
                  {formData.ghlLocation_id && formData.ghlLocation_id !== 'none'
                    ? locations.find(l => l.id === formData.ghlLocation_id)?.name.replace(/"/g, '') || 'Selected'
                    : 'Not assigned'
                  }
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmModal(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button 
              onClick={confirmSave}
              className="bg-[#d4af37] hover:bg-[#b8941e]"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
